import { usePage, router } from "@inertiajs/react";
import { useCallback, useRef } from "react";
import type { StreamSnapshot } from "@launch10/use-langgraph-stream";
import { useStreamSelector } from "@launch10/use-langgraph-stream";
import type { AdsGraphState } from "@shared";
import { useProjectStreamOptions } from "@hooks/useProjectStreamOptions";

interface AdsPageProps {
  thread_id?: string;
  campaign?: { id: number; [key: string]: unknown };
  [key: string]: unknown;
}

export type AdsSnapshot = StreamSnapshot<AdsGraphState>;

/**
 * Build stream options for the ads chat.
 *
 * Uses useProjectStreamOptions for all centralized config (JWT, apiUrl,
 * headers, threadId safety, reconnect, onUpdateEvent). Only adds the
 * ads-specific threadId handler.
 */
export function useAdsStreamOptions() {
  const page = usePage<AdsPageProps>();
  const pagePropsRef = useRef(page.props);
  pagePropsRef.current = page.props;

  const onThreadId = useCallback((threadId: string) => {
    if (!threadId || threadId === "undefined" || threadId === "null") {
      console.error(
        "[useAdsChat] onThreadId called with invalid threadId:",
        threadId
      );
      return;
    }

    // Preserve the current query string (notably `?campaign_id=N` in the
    // multi-campaign path). Dropping it sends router.reload() requests on
    // stream-end to a URL that resolves to the oldest campaign instead of
    // the one the agent just wrote to — so fresh headlines never appear in
    // the form even though they're in the DB.
    router.push({
      url: window.location.pathname + window.location.search,
      preserveState: true,
      component: "Ads",
      props: {
        ...pagePropsRef.current,
        thread_id: threadId,
      },
    });
  }, []);

  return useProjectStreamOptions<AdsGraphState>({
    assistantId: "ads",
    onThreadId,
  });
}

// ---------------------------------------------------------------------------
// Selectors — must be used inside Chat.Root (SmartStreamProvider)
// ---------------------------------------------------------------------------

export function useAdsSelector<T>(selector: (s: AdsSnapshot) => T): T {
  return useStreamSelector<T, AdsGraphState>(selector);
}

export function useAdsChatMessages() {
  return useAdsSelector((s) => s.messages);
}

export function useAdsChatState<K extends keyof AdsGraphState>(key: K) {
  return useAdsSelector((s) => s.values?.[key]);
}

/** True while the agent is compacting conversation history (see CompactingIndicator). */
export function useAdsChatIsCompacting() {
  return useAdsSelector((s) => s.values?.isCompacting ?? false);
}

export function useAdsChatFullState() {
  return useAdsSelector((s) => s.values);
}

export function useAdsChatIsStreaming() {
  return useAdsSelector((s) => s.isLoading);
}

export function useAdsChatIsLoadingHistory() {
  return useAdsSelector((s) => s.isThreadLoading);
}

export function useAdsChatIsReady() {
  return useAdsSelector((s) => !s.isThreadLoading && !s.isLoading);
}

export function useAdsChatSubmit() {
  return useAdsSelector((s) => s.submit);
}

/**
 * Returns only submit — no more setState/saveState.
 * The graph writes assets to Rails directly via saveAssets node.
 */
export function useAdsChatActions() {
  const submit = useAdsChatSubmit();
  return { submit };
}
